import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, Circle, Loader2, Activity } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

const formatDate = (iso) => {
    if (!iso) return "–";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric",
        });
    } catch { return iso; }
};

export const ConstructionTracker = () => {
    const t = useT();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/construction")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <section className="bg-ink py-20" data-testid="construction-tracker-loading">
                <div className="container-page flex justify-center">
                    <Loader2 className="text-clay-300 animate-spin" size={24} />
                </div>
            </section>
        );
    }
    if (!data) return null;

    const phases = data.phases || [];
    const currentIdx = phases.findIndex((p) => p.value === data.phase);
    const overallProgress = currentIdx >= 0
        ? Math.min(100, ((currentIdx) / Math.max(phases.length - 1, 1)) * 100 + (data.progress / 100) * (100 / Math.max(phases.length - 1, 1)))
        : 0;

    return (
        <section
            id="bauphase"
            className="bg-ink text-white py-20 sm:py-28"
            data-testid="construction-tracker-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="relative w-2 h-2 rounded-full bg-green-400">
                                <span className="absolute inset-0 rounded-full bg-green-400 animate-ping" />
                            </div>
                            <span className="overline text-clay-300">{t({ de: "Live · Aktueller Stand", en: "Live · Current status" })}</span>
                        </div>
                        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-[1.05] text-balance">
                            {t({ de: "Wir bauen", en: "We build" })}
                            <span className="italic text-clay-300"> {t({ de: "transparent.", en: "transparently." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="font-body text-base text-white/75 leading-relaxed">
                            {t({
                                de: "Sie sehen jederzeit, in welcher Phase sich Ihr neues Zuhause befindet. Live-Update vom Bauträger – kein Rätselraten.",
                                en: "You can see at any moment which phase your new home is in. A live update from the developer – no guesswork.",
                            })}
                        </p>
                    </div>
                </div>

                {/* Current phase headline + progress */}
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
                    <div className="lg:col-span-7 bg-white/5 border border-white/10 p-7 lg:p-9">
                        <div className="flex items-center gap-3 mb-3">
                            <Activity size={18} className="text-clay-300" />
                            <span className="overline text-clay-300">{t({ de: "Aktuelle Phase", en: "Current phase" })}</span>
                        </div>
                        <h3
                            className="font-heading text-3xl text-white mb-2 flex items-center gap-3 flex-wrap"
                            data-testid="construction-current-phase"
                        >
                            {data.phase_label}
                            {data.progress >= 100 && (
                                <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/15 border border-green-400/40 text-green-300 text-xs font-body font-medium tracking-wide"
                                    data-testid="phase-done-badge"
                                >
                                    <CheckCircle2 size={13} strokeWidth={2.2} className="text-green-400" />
                                    {t({ de: "Abgeschlossen", en: "Completed" })}
                                </span>
                            )}
                        </h3>
                        {data.description && (
                            <p className="font-body text-sm text-white/75 leading-relaxed mb-6">
                                {data.description}
                            </p>
                        )}
                        <div className="mt-6">
                            <div className="flex items-baseline justify-between mb-2">
                                <span className="overline text-white/60">{t({ de: "Fortschritt dieser Phase", en: "Progress of this phase" })}</span>
                                <span className="font-heading text-2xl text-clay-300" data-testid="construction-progress">
                                    {data.progress}%
                                </span>
                            </div>
                            <div className="h-1.5 bg-white/10 overflow-hidden">
                                <div
                                    className="h-full bg-clay-300 transition-all duration-1000"
                                    style={{ width: `${data.progress}%` }}
                                />
                            </div>
                        </div>
                        <p className="font-body text-xs text-white/50 mt-4">
                            {t({ de: "Letzte Aktualisierung:", en: "Last updated:" })} {formatDate(data.updated_at)}
                        </p>
                    </div>

                    {/* Overall project progress */}
                    <div className="lg:col-span-5 bg-clay-300/10 border border-clay-300/20 p-7 lg:p-9 flex flex-col justify-center">
                        <span className="overline text-clay-300">{t({ de: "Projekt-Fortschritt", en: "Overall progress" })}</span>
                        <div className="font-heading text-7xl text-clay-300 mt-2" data-testid="construction-overall">
                            {Math.round(overallProgress)}%
                        </div>
                        <p className="font-body text-sm text-white/65 mt-2">
                            {t({ de: "Phase", en: "Phase" })} {Math.max(currentIdx + 1, 1)} {t({ de: "von", en: "of" })} {phases.length}
                        </p>
                    </div>
                </div>

                {/* Phases stepper */}
                <div className="border-t border-white/10 pt-10">
                    <ol className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 lg:gap-2" data-testid="construction-phases-list">
                        {phases.map((p, i) => {
                            const isCurrent = i === currentIdx;
                            const isPast = i < currentIdx;
                            return (
                                <li
                                    key={p.value}
                                    className={`relative pt-4 ${
                                        isPast ? "opacity-90" : isCurrent ? "" : "opacity-50"
                                    }`}
                                    data-testid={`construction-phase-${p.value}`}
                                >
                                    {/* Connector line */}
                                    {i < phases.length - 1 && (
                                        <span
                                            className={`hidden lg:block absolute top-2 left-[calc(50%+12px)] right-[-50%] h-px ${
                                                isPast ? "bg-clay-300" : "bg-white/15"
                                            }`}
                                            aria-hidden
                                        />
                                    )}
                                    <div className="flex flex-col items-start lg:items-center text-left lg:text-center">
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                            isPast ? "bg-clay-300 text-ink" : isCurrent ? "ring-2 ring-clay-300 ring-offset-2 ring-offset-ink bg-ink" : "bg-white/15"
                                        }`}>
                                            {isPast ? (
                                                <CheckCircle2 size={14} strokeWidth={2} />
                                            ) : isCurrent ? (
                                                <span className="w-1.5 h-1.5 rounded-full bg-clay-300" />
                                            ) : (
                                                <Circle size={10} strokeWidth={1.5} className="text-white/50" />
                                            )}
                                        </div>
                                        <div className={`mt-3 font-body text-[11px] uppercase tracking-[0.15em] ${
                                            isCurrent ? "text-clay-300 font-semibold" : isPast ? "text-white/85" : "text-white/45"
                                        }`}>
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                        <div className={`font-body text-xs lg:text-[12px] mt-1 leading-tight ${
                                            isCurrent ? "text-white font-medium" : "text-white/65"
                                        }`}>
                                            {p.label}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </div>
        </section>
    );
};
